import { defineStore } from 'pinia';
import TeasersService from '@/services/teasers';
import _ from 'lodash';
import { useEventBus } from '@vueuse/core';
import { useBotsStore } from '@/stores/bots';
import { useIntentsStore } from '@/stores/intents';

/** Lowercase english day name */
type DayOfWeek = 'sunday' | 'monday' | 'tuesday' | 'wednesday' | 'thursday' | 'friday' | 'saturday';

/** Hour constraint for one day */
interface TimeConstraintHour {
  /** Day of the week as index (0 = Sunday, 1 = Monday, ..., 6 = Saturday). Follows JS getDay() method */
  day: number;
  /** Minute index for start time (e.g. 480 for 08:00) */
  from: number;
  /** Minute index for end time (e.g. 1112 for 18:32) */
  to: number;
}

interface TimeConstraintCampaign {
  /** ISO date string for campaign start (e.g. '2024-06-15T00:00:00.000Z') or null for no start */
  from: string | null;
  /** ISO date string for campaign end (e.g. '2024-07-15T23:59:59.999Z') or null for no end */
  to: string | null;
}

interface TimeConstraint {
  /** Campaign time constraint */
  campaign: TimeConstraintCampaign;
  /**
   * Array of hour constraints per day.
   * If empty, no day+time based restrictions. Else, only the defined days and hours are allowed.
   */
  days: TimeConstraintHour[];
}

/** Normalized per-day constraint for easier use in the UI */
interface NormalizedTimeConstraintDay {
  /** Whether the day is enabled */
  enabled: boolean;
  /** Time string for start time (e.g. '08:00') */
  from: string;
  /** Time string for end time (e.g. '18:32') */
  to: string;
}

interface NormalizedConstraintCampaign {
  /** Whether the campaign is enabled */
  enabled: boolean;
  /** Simple date strings for campaign start/end (e.g. ['2024-06-15', '2024-07-15']) */
  range: [string, string];
}

/** Teaser object as stored in the backend (loose shape; payload is backend-owned) */
interface Teaser {
  /** Unique teaser id (`teaser_<timestamp>_<channelId>`) */
  id: string;
  /** Target device: 'both' | 'desktop' | 'mobile' */
  device?: string;
  /** Whether the teaser is live */
  active?: boolean;
  /** Widget teaser payload (message, quickActions, channelId, displayName, ...) */
  payload?: any;
  /** URL allow list (mutually exclusive with blockList) */
  allowList?: string[];
  /** URL block list (mutually exclusive with allowList) */
  blockList?: string[];
  /** Time based display constraints */
  timeConstraints?: TimeConstraint;
  [key: string]: any;
}

/** One incomplete setting of the edited teaser. `action` is the 1-based number of the affected action. */
export interface TeaserBlockedReason {
  key: 'channel' | 'message' | 'actionText' | 'actionTarget' | 'actionTargetNotLive' | 'actionQuestion' | 'schedule';
  action?: number;
}

interface TeasersState {
  /** All teasers of the current bot across its channels */
  teasers: Teaser[];
  /** Teaser currently being edited (working copy) */
  teaser: Teaser;
  /** Snapshot of the edited teaser for dirty checking / reset */
  teaserInitial: Teaser;
  /** True when `teaser` differs from `teaserInitial` */
  isDirty: boolean;
  /** False when the edited teaser is incomplete/invalid (see checkIsBlocked) */
  isValid: boolean;
  /** Why the edited teaser is blocked, as set by checkIsBlocked (empty when valid) */
  blockedReasons: TeaserBlockedReason[];
  /** channelId of the currently selected channel, null = none */
  selectedChannel: string | null;
}

function remapTeaserObjects(teasers: Teaser[]) {
  teasers = teasers.map((t) => {
    t.payload.quickActions = t.payload.quickActions.map((q: any) => {
      if (q.targetIntent) {
        q.intent = q.targetIntent;
      }
      return q;
    });
    return t;
  });
  return teasers;
}

/** Minimum length of the simulated user question an agent-targeting teaser button sends. */
export const TARGET_INSTRUCTION_MIN_LENGTH = 20;

const utils = {
  /**
   * Converts a time like 08:00 or 18:32 into a minute index
   * @param {string} time E.g. '08:00' or '18:32'
   * @returns {number}
   */
  parseTime(time: string): number {
    const [hours, minutes] = time.split(':').map((t) => parseInt(t));
    return hours * 60 + minutes;
  },
  /**
   * Converts a minute index to a time string like 08:00 or 18:32
   * @param {number} minuteIndex E.g. 480 or 1112
   * @returns {string} E.g. '08:00' or '18:32'
   */
  serializeTime(minuteIndex: number): string {
    const hours = Math.min(Math.floor(minuteIndex / 60), 23);
    const minutes = Math.min(minuteIndex % 60, 59);
    return `${hours.toString().padStart(2, '0')}:${minutes
      .toString()
      .padStart(2, '0')}`;
  },
  /**
   * Converts an ISO date to a simple string that Vuetify can use
   * @param {string} [isoDate] E.g. `2024-06-15T00:00:00.000Z`. Defaults to now
   * @returns {string} E.g. `2024-06-15`
   */
  parseISODate(isoDate?: string | null): string {
    const date = new Date(isoDate || Date.now());

    const year = date.getUTCFullYear();
    const month = (date.getUTCMonth() + 1).toString().padStart(2, '0');
    const day = date.getUTCDate().toString().padStart(2, '0');

    return `${year}-${month}-${day}`;
  },
  /**
   * Converts a simple date string to an ISO date string
   * @param {string} simpleDate E.g. `2024-06-15`
   * @returns {string} E.g. `2024-06-15T00:00:00.000Z`
   */
  serializeISODate(simpleDate: string): string {
    /**
     * NOTE: We assume that the times in the dates are always 00:000:00.000Z.
     * This way we won't have subtle time differences causing isDirty checks
     * to return true.
     */
    return new Date(simpleDate).toISOString();
  },
  /**
   * Map of day name to day index
   */
  dayToIndex: {
    sunday: 0,
    monday: 1,
    tuesday: 2,
    wednesday: 3,
    thursday: 4,
    friday: 5,
    saturday: 6,
  } as Record<DayOfWeek, number>,
  /**
   * Map of day index to day name
   */
  indexToDay: {
    0: 'sunday',
    1: 'monday',
    2: 'tuesday',
    3: 'wednesday',
    4: 'thursday',
    5: 'friday',
    6: 'saturday',
  } as Record<number, DayOfWeek>,
};

/**
 * Pinia conversion of legacy vuex module `src/store/modules/teasers.ts` (namespace `teasers`).
 * In-memory only — no persistence in the legacy module, so no `vuexPersist` option here either.
 *
 * Cross-namespace access (see
 * `/private/tmp/claude-501/-Users-haslien-Documents-GitHub-moin-hub/23091b27-f9d9-4b85-91e6-ff6cd1c12710/scratchpad/pinia-migration/STORES.md`):
 * - `rootGetters['bots/currentChannels']` → `useBotsStore().currentChannels` (called inside the
 *   action body, per module).
 * - `rootGetters['intents/intents']` → `useIntentsStore().intents` (called inside `checkIsBlocked`).
 *
 * Name collisions (state prop + getter shared the same name, which Vuex allows via separate
 * registries but Pinia does not — the getter would shadow the state prop with a readonly
 * computed): `teasers`, `teaser`, `isDirty`, `isValid`, `selectedChannel` getters renamed to
 * `teasersGetter`, `teaserGetter`, `isDirtyGetter`, `isValidGetter`, `selectedChannelGetter`
 * respectively (see `<storeId>Getter` naming rule in DESIGN.md; recorded in
 * `reports/teasers.json`). Internal actions read/write the state props directly via
 * `this.teasers`/`this.teaser`/`this.isDirty`/`this.isValid`/`this.selectedChannel`, exactly as
 * the legacy mutations/actions used `state.x` directly (never `getters.x`) for these five.
 * No other getter/action/mutation name collisions exist in this module (mutation names don't
 * overlap any other getter/action name), so every other legacy mutation becomes a same-named
 * action with no renames.
 */
export const useTeasersStore = defineStore('teasers', {
  state: (): TeasersState => ({
    teasers: [],
    teaser: { id: '' },
    teaserInitial: { id: '' },
    isDirty: false,
    isValid: true,
    blockedReasons: [],
    selectedChannel: null,
  }),
  getters: {
    /** Renamed from `teasers` — collided with the state prop of the same name. */
    teasersGetter: (state: TeasersState) => state.teasers,
    /** Renamed from `teaser` — collided with the state prop of the same name. */
    teaserGetter: (state: TeasersState) => {
      if (!state.teaser) return null;
      if (!state.teaser?.timeConstraints) {
        state.teaser.timeConstraints = { campaign: { from: null, to: null }, days: [] };
      }

      return state.teaser;
    },
    /** Renamed from `isDirty` — collided with the state prop of the same name. */
    isDirtyGetter: (state: TeasersState) => state.isDirty,
    /** Renamed from `isValid` — collided with the state prop of the same name. */
    isValidGetter(state: TeasersState): boolean {
      return state.isValid && !this.invalidDays.length;
    },
    /** What is missing before the edited teaser can be saved and activated, in page order. */
    missingSettings(state: TeasersState): TeaserBlockedReason[] {
      const reasons = [...state.blockedReasons];
      if (this.invalidDays.length) reasons.push({ key: 'schedule' });
      return reasons;
    },
    /** Renamed from `selectedChannel` — collided with the state prop of the same name. */
    selectedChannelGetter: (state: TeasersState) => state.selectedChannel,
    /**
     * Normalized time constraints for easier use in the UI
     * @param state
     * @returns {null | NormalizedTimeConstraints}
     */
    timeConstraints: (state: TeasersState): null | { days: Record<DayOfWeek, NormalizedTimeConstraintDay>; campaign: NormalizedConstraintCampaign } => {
      const t = state.teaser?.timeConstraints;
      if (!t) {
        console.error(`Teaser was expected to be initialized already with timeConstraints, but it was not`);
        return null;
      }

      // Base map of days for the table
      const days: Record<DayOfWeek, NormalizedTimeConstraintDay> = {
        monday:     { enabled: false, from: "08:00", to: "18:00" },
        tuesday:    { enabled: false, from: "08:00", to: "18:00" },
        wednesday:  { enabled: false, from: "08:00", to: "18:00" },
        thursday:   { enabled: false, from: "08:00", to: "18:00" },
        friday:     { enabled: false, from: "08:00", to: "18:00" },
        saturday:   { enabled: false, from: "08:00", to: "18:00" },
        sunday:     { enabled: false, from: "08:00", to: "18:00" },
      };

      // Populate any days that are enabled
      for (const hourConstraint of t.days || []) {
        const dayName = utils.indexToDay[hourConstraint.day];
        if (!days[dayName]) continue;

        days[dayName] = {
          enabled: true,
          from: utils.serializeTime(hourConstraint.from),
          to: hourConstraint.to >= 24*60
            ? "00:00"
            : utils.serializeTime(hourConstraint.to),
        };
      }

      // Set up the campaign data
      const campaign: NormalizedConstraintCampaign = { enabled: false, range: [utils.parseISODate(), utils.parseISODate()] };
      if (!!t.campaign.from && t.campaign.to) {
        campaign.enabled = true;
        campaign.range = [
          utils.parseISODate(t.campaign.from),
          utils.parseISODate(t.campaign.to),
        ];
      }

      return { days, campaign };
    },
    /**
     * Checks which days are invalid in the time constraints
     * @param state
     * @returns {Array<DayOfWeek>} Array of day names that are invalid
     */
    invalidDays(state: TeasersState): DayOfWeek[] {
      const arr = state.teaser?.timeConstraints?.days || [];
      const invalidDays: DayOfWeek[] = [];

      for (const hourConstraint of arr) {
        // The "to" must be greater than "from"
        if (hourConstraint.to <= hourConstraint.from) {
          invalidDays.push( utils.indexToDay[hourConstraint.day] );
        }
      }

      return invalidDays;
    },
  },
  actions: {
    async loadTeasers({ botId }: { botId: string }) {
      const currentChannels = useBotsStore().currentChannels;
      const loadedTeasers: Teaser[] = [];
      for (let c of currentChannels) {
        const teasers = await TeasersService.getTeasers(botId, c.channelId);
        loadedTeasers.push(...teasers);
      }

      // Populate some props to make diff detection simpler
      for (let i=0; i<loadedTeasers.length; i++) {
        if (loadedTeasers[i].timeConstraints) continue;
        loadedTeasers[i].timeConstraints = {campaign: {from: null, to: null}, days: []};
      }

      this.teasers = loadedTeasers;
    },
    async loadTeaser({ botId, teaserId }: { botId: string; teaserId: string }) {
      await this.loadTeasers({ botId });
      if (!this.teasers?.length) {
        return false;
      }
      const t = this.teasers.find((t: Teaser) => t.id === teaserId);
      if (!t) return false;

      if (!t?.payload?.displayName) {
        t.payload.displayName = '';
      }

      this.teaser = _.cloneDeep(t);
      this.teaserInitial = _.cloneDeep(t);

      return true;
    },
    async saveTeasers({ botId, teasers }: { botId: string; teasers: Teaser[] }) {
      teasers = remapTeaserObjects(teasers);
      const currentChannels = useBotsStore().currentChannels;
      const savedTeasers: Teaser[] = [];

      for (const c of currentChannels) {
        const channelTeasers = teasers.filter((t) => t.payload.channelId === c.channelId);

        let savedChannelTeasers = await TeasersService.saveTeasers({
          botId,
          channelId: c.channelId,
          teasers: channelTeasers,
        });

        savedTeasers.push(...savedChannelTeasers);
      }

      this.teasers = savedTeasers;
    },
    async saveTeaser({ botId }: { botId: string }) {
      const teasers = _.cloneDeep(this.teasers);

      // Cleanup empty lists
      if (this.teaser?.allowList && this.teaser?.allowList.length === 0) {
        delete this.teaser.allowList;
      }

      if (
        this.teaser?.payload?.blockList &&
        this.teaser?.payload?.blockList.length === 0
      ) {
        delete this.teaser.payload.blockList;
      }

      // Cleanup lists
      if (this.teaser?.allowList?.length) {
        this.teaser.allowList = this.teaser.allowList.map((url: string) => {
          url = url.replace('https://', '');
          url = url.replace('http://', '');
          return url.trim();
        });
      }

      if (this.teaser?.blockList?.length) {
        this.teaser.blockList = this.teaser.blockList.map((url: string) => {
          url = url.replace('https://', '');
          url = url.replace('http://', '');
          return url.trim();
        });
      }

      const index = teasers.findIndex((t: Teaser) => t.id === this.teaser.id);
      teasers[index] = this.teaser;
      this.saveTeasers({ botId, teasers });

      this.teaserInitial = _.cloneDeep(this.teaser);
      this.checkIsDirty();

      return true;
    },
    async createTeaser(
      { botId, channelId, displayName = '', message, quickAction }: { botId: string; channelId: string; displayName?: string; message: string; quickAction: any }
    ) {
      const newTeaser = {
        id: `teaser_${new Date().getTime()}_${channelId}`,
        device: 'both',
        payload: {
          showTeaser: true,
          showAvatar: true,
          message,
          timeout: 1000,
          quickActions: [quickAction],
          displayName,
          channelId,
        },
        active: false,
      };

      let teasers = _.cloneDeep(this.teasers);

      teasers.push(newTeaser);
      this.saveTeasers({ botId, teasers });
      return true;
    },
    async deleteTeaser({ botId, teaserId }: { botId: string; teaserId: string }) {
      let teasers = _.cloneDeep(this.teasers);

      const index = teasers.findIndex((t: Teaser) => t.id === teaserId);
      if (index < 0) return;
      teasers.splice(index, 1);
      this.saveTeasers({ botId, teasers });
    },
    resetToInitial() {
      this.setTeaser(_.cloneDeep(this.teaserInitial));
      this.checkIsDirty();
    },
    /**
     * Like the `resetToInitial`, except it targets just the time constraints
     */
    resetTimeConstraints() {
      const timeConstraints = _.cloneDeep(this.teaserInitial.timeConstraints);
      this.setTeaser({ ...this.teaser, timeConstraints });
      this.checkIsDirty();
    },
    checkIsBlocked() {
      const intents = (useIntentsStore().intents || []) as any[];
      const channelId = this.teaser?.payload?.channelId;
      const reasons: TeaserBlockedReason[] = [];

      if (!channelId) reasons.push({ key: 'channel' });
      if (!this.teaser?.payload?.message) reasons.push({ key: 'message' });

      this.teaser?.payload?.quickActions?.forEach((a: any, index: number) => {
        const action = index + 1;
        if (!a.text) reasons.push({ key: 'actionText', action });
        if (!a.targetIntent) reasons.push({ key: 'actionTarget', action });

        if (a?.intent !== 'start') {
          const intent = intents.find((i: any) => i.intent === a.intent);
          const state = intent?.state?.[channelId];
          if (state && state.state !== 'success') reasons.push({ key: 'actionTargetNotLive', action });
        }

        const targetIntent = intents.find((i: any) => i.intent === a.targetIntent);
        if (
          targetIntent?.template === 'RAGTopic' &&
          (a.targetInstruction || '').trim().length < TARGET_INSTRUCTION_MIN_LENGTH
        ) {
          reasons.push({ key: 'actionQuestion', action });
        }
      });

      this.blockedReasons = reasons;
      this.setIsValid(!reasons.length);
    },
    checkIsDirty() {
      if (
        !_.isEqual(this.teaser, this.teaserInitial) ||
        !_.isEqual(this.teaser?.payload, this.teaserInitial?.payload) ||
        !_.isEqual(
          this.teaser?.payload?.quickActions,
          this.teaserInitial?.payload?.quickActions
        ) ||
        !_.isEqual(this.teaser?.allowList, this.teaserInitial?.allowList) ||
        // TODO device key name correct?!
        !_.isEqual(
          this.teaser?.device,
          this.teaserInitial?.device
        )
      ) {
        this.isDirty = true;
        return;
      }
      this.isDirty = false;
    },
    setTeaser(teaser: Teaser) {
      if (!teaser?.timeConstraints) {
        teaser.timeConstraints = {campaign: {from: null, to: null}, days: []};
      }

      this.teaser = teaser;

      // We need to notify of reset in some components, so they can re-calculate/remount
      useEventBus('teasers').emit('reset', { id: teaser.id });
    },
    setTimeout(newTimeout: number) {
      const newTeaser = this.teaser;
      if (!newTeaser?.payload) return;
      newTeaser.payload.timeout = newTimeout;
      this.teaser = newTeaser;
    },
    setSelectedChannel(channelId: string | null) {
      this.selectedChannel = channelId;
    },
    addAction() {
      if (!this.teaser?.payload) return;
      this.teaser.payload.quickActions.push({
        id: `teaser_action_${new Date().getTime()}_${this.teaser.payload.channelId}`,
        text: '',
        intent: '',
      });
    },
    setIsValid(isValid: boolean) {
      this.isValid = isValid;
    },
    deleteAction(actionId: string) {
      const index = this.teaser.payload.quickActions.findIndex(
        (qa: any) => qa.id === actionId
      );
      if (index >= 0) this.teaser.payload.quickActions.splice(index, 1);
    },
    updateAllowList(allowList: string[]) {
      delete this.teaser.blockList;
      this.teaser.allowList = allowList;
    },
    updateBlockList(blockList: string[]) {
      delete this.teaser.allowList;
      this.teaser.blockList = blockList;
    },
    deleteIntegrationLists() {
      delete this.teaser.allowList;
      delete this.teaser.blockList;
    },
    updateIgnoreTrailingSlash(ignore: boolean) {
      this.teaser.ignoreTrailingSlash = Boolean(ignore);
    },
    setQuickActions(actions: any[]) {
      this.teaser.payload.quickActions = _.cloneDeep(actions);
    },
    /**
     * Moves a teaser within `scopeIds` (the teasers visible in the list, e.g. one channel). Only the scope's
     * relative order changes; teasers outside the scope (other channels) keep their slots. Without a scope, the
     * whole list is reordered.
     */
    moveTeaser({
      teaserId,
      to,
      scopeIds,
    }: {
      teaserId: string;
      to: 'top' | 'up' | 'down' | 'bottom';
      scopeIds?: string[];
    }) {
      const scope = scopeIds ?? this.teasers.map((t) => t.id);
      const from = scope.indexOf(teaserId);
      if (from < 0) return;

      const target = { top: 0, up: from - 1, down: from + 1, bottom: scope.length - 1 }[to];
      if (target < 0 || target >= scope.length || target === from) return;

      const reordered = [...scope];
      reordered.splice(from, 1);
      reordered.splice(target, 0, teaserId);

      const inScope = new Set(scope);
      const byId = new Map(this.teasers.map((t) => [t.id, t]));
      let next = 0;
      this.teasers = this.teasers.map((t) => (inScope.has(t.id) ? byId.get(reordered[next++]!)! : t));
    },
    setTeaserActive({ teaserId, isActive }: { teaserId: string; isActive: boolean }) {
      // non-null as in the JS version: caller guarantees the teaser exists
      this.teasers.find((t) => t.id === teaserId)!.active = isActive;
    },
    setTeaserDevice(deviceType: string) {
      const newTeaser = this.teaser;
      if (!newTeaser) return;
      newTeaser.device = deviceType;
      this.teaser = newTeaser;
    },
    duplicateTeaser({ teaser, lang }: { teaser: Teaser; lang: string }) {
      const newTeaser = _.cloneDeep(teaser);
      newTeaser.active = false;
      newTeaser.id = `teaser_${new Date().getTime()}_${teaser.payload.channelId}`;
      newTeaser.payload.displayName += ` (${lang === 'en' ? 'copy' : 'Kopie'})`;
      // change teaserAction
      newTeaser.payload.quickActions.forEach((action: any, index: number) => {
        const uniqueTime = new Date().getTime() + index;
        action.id = `teaser_action_${uniqueTime}_${teaser.payload.channelId}`;
      });
      this.teasers.push(newTeaser);
    },

    /**
     * Sets the campaign constraint in the teaser time constraints
     * @param campaign {NormalizedConstraintCampaign}
     */
    setCampaignState(campaign: any = {}) {
      // Remove the campaign
      if (!campaign.enabled || !campaign.range[0] || !campaign.range[1]) {
        this.teaser.timeConstraints!.campaign = { from: null, to: null };
        return;
      }

      // Enable the campaign
      this.teaser.timeConstraints!.campaign = {
        from: utils.serializeISODate(campaign.range[0]),
        to: utils.serializeISODate(campaign.range[1]),
      };
    },
    /**
     * Sets or removes a day in the teaser time constraints
     * @param {DayOfWeek} dayOfWeek
     * @param {NormalizedTimeConstraintDay} day
     */
    setActiveDay({ dayOfWeek, day }: { dayOfWeek: DayOfWeek; day: NormalizedTimeConstraintDay } ) {
      const dayIndex = utils.dayToIndex?.[dayOfWeek];
      if (dayIndex === undefined) return;

      // Remove the day
      if (!day.enabled) {
        this.teaser.timeConstraints!.days = this.teaser.timeConstraints!.days.filter(hc => hc.day !== dayIndex);
        return;
      }

      // Add or update the day
      const fromIndex = Math.min( utils.parseTime(day.from||'08:00'), 24*60-1 );
      const toIndex = Math.min( utils.parseTime(day.to||'16:00') || 24*60, 24*60);
      /**
       * UX: 00:00 becomes index 0. In "from" that means start of day, but in "to" it means end of day.
       * An easy workaround is to set it to 1440 (24*60) in "to" case if it is resolving to 0,
       */

      const existingIndex = this.teaser.timeConstraints!.days.findIndex(hc => hc.day === dayIndex);
      if (existingIndex >= 0) {
        // Update existing
        this.teaser.timeConstraints!.days[existingIndex].from = fromIndex;
        this.teaser.timeConstraints!.days[existingIndex].to = toIndex;
        return;
      }

      // Add new
      this.teaser.timeConstraints!.days.push({
        day: dayIndex,
        from: fromIndex,
        to: toIndex,
      });
    },
  },
});
